import {
    daysCantBeInThePast,
    formatDate,
    isGiftOfferProduct,
    nullableEmail,
    requiredEmail,
    requiredNumber,
    requiredNumberNotEquals,
    requiredString,
} from "common";
import { useEffect } from "react";
import { Affiliate, GiftOffer, GiftOfferType, PartnerObject, PaymentMethod, PurchaseRule } from "types";
import { AnySchema, array, number, string } from "yup/lib";
import shallow from "zustand/shallow";
import { isCommentRequired } from "../../../models/Payment";
import { useCustomerDetailsStore } from "../store/customerDetailsStore";
import { usePurchaseResponseStore } from "../store/usePurchaseResponseStore";
import { useRecipientRules } from "./useGiftDetailsRequirements";
import { api } from "@/api/api";
import { queryClient } from "@/api/queryClient";
import { getTerms } from "@/components/Product/EditGiftOffer";
import { useResource } from "@/hooks/useResource";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";
import { getSeller } from "@/utils/getSeller";

type CustomerFields = "customerName" | "customerEmail" | "phone";

type DepositFields = "deposit" | "depositDuration";

type InvoiceFields = "arrivalOffset";

type GiftFields = "recipientName" | "recipientEmail" | "amount";

type FieldValidations<T extends string> = {
    [key in T]: AnySchema;
};

export type CheckoutPurchaseScreenValidations = FieldValidations<
    DepositFields | InvoiceFields | "tags" | "note" | "comment"
> &
    Partial<FieldValidations<CustomerFields>> &
    Partial<FieldValidations<GiftFields>>;

/**
 * Exports the validation schema for checkout (payment) screen form.
 **/
export const useCheckoutPurchaseScreenValidations = (): any => {
    const seller = getSeller();
    const [paymentMethod] = useCreatePurchaseStore(
        (state) => [state.paymentLineItem.payment.method, formatDate(state.getEarliestArrivalDateTime())],
        shallow,
    );
    const commentRequired = isCommentRequired(seller, paymentMethod);

    const customerValidations = useCustomerFieldsValidations(paymentMethod as PaymentMethod);
    const depositValidations = useDepositFieldsValidations(paymentMethod as PaymentMethod);
    const invoiceValidations = useInvoiceFieldsValidations(paymentMethod as PaymentMethod);

    return {
        ...customerValidations,
        ...depositValidations,
        ...invoiceValidations,
        tags: array().optional(),
        note: array().optional(),
        comment: commentRequired ? requiredString() : string().trim(),
    };
};

const fetchAffiliate = async (partnerId: ID): Promise<Affiliate | null> => {
    try {
        const { data } = await api.get<Affiliate>(`/affiliates/${partnerId}`);
        return data;
    } catch (error) {
        console.error("Failed to fetch affiliate", error);
        return null;
    }
};

export const useCustomerFieldsValidations = (
    paymentMethod: PaymentMethod,
): Partial<FieldValidations<CustomerFields>> => {
    const hasPurchasePartnerAffiliate = usePurchaseResponseStore(
        (state) => !!state.purchase.partner?.id && state.purchase.partner?.object === PartnerObject.AFFILIATE,
    );

    const organizerExists = useCreatePurchaseStore((state) => !!state.organizer);

    const partnerId = usePurchaseResponseStore.getState().purchase.partner?.id;

    const [nameRequired, emailRequired, phoneRequired, showCustomerFields] = useCustomerDetailsStore(
        (state) => [state.nameRequired, state.emailRequired, state.phoneRequired, state.showCustomerFields],
        shallow,
    );

    const isInvoicePurchase = paymentMethod === PaymentMethod.INVOICE;

    const affiliate = hasPurchasePartnerAffiliate
        ? queryClient.getQueryData<Affiliate>(["affiliates", partnerId])
        : null;

    const shouldFetchAffiliate = hasPurchasePartnerAffiliate && !affiliate;

    // Using axios fetch to get the affiliate data instead of useResource as this hook is inside the parent component CheckoutPurchaseScreen and it causes delay when using useResource.
    // We are setting the data in queryClient to avoid multiple calls and reusing the cached data.
    useEffect(() => {
        if (shouldFetchAffiliate && partnerId) {
            fetchAffiliate(partnerId).then((data) => {
                queryClient.setQueryData(["affiliates", partnerId], data);
            });
        }
    }, [shouldFetchAffiliate, partnerId]);

    if (!showCustomerFields) {
        return {};
    }

    const isAffiliateNotificationEnabled = !!affiliate?.affiliateNotification?.enabled;

    if (organizerExists) {
        return {
            customerEmail: isInvoicePurchase && !isAffiliateNotificationEnabled ? requiredEmail() : string(),
        };
    }

    const isNameRequired = !!nameRequired && showCustomerFields;
    const isEmailRequired =
        showCustomerFields && (emailRequired || isInvoicePurchase) && !isAffiliateNotificationEnabled;
    const isPhoneRequired = showCustomerFields && phoneRequired;

    return getCustomerValidations(isNameRequired, isEmailRequired, isPhoneRequired);
};

export const useGiftFieldsValidations = (): Partial<FieldValidations<GiftFields>> => {
    const product: GiftOffer = useCreatePurchaseStore((state) => state.getActiveProduct());
    const { purchaseRule: purchaseRuleFromProduct } = product;

    const { data: fetchedPurchaseRule } = useResource<PurchaseRule>(["purchaseRules", product?.purchaseRule?.id]);

    const purchaseRule = purchaseRuleFromProduct?.actions ? purchaseRuleFromProduct : fetchedPurchaseRule;

    const isNoGiftExperience = !product?.experience?.id;
    const isChooseAnAmount = product.baseGiftProduct && product.system;
    const giftTerms = getTerms(product, purchaseRule);
    const isVariable = giftTerms.type === GiftOfferType.VARIABLE;
    const { isRecipientRequired: recipientRequired } = useRecipientRules();

    const minimumPrice =
        isVariable && giftTerms.minimumPrice && giftTerms.minimumPrice > 0 ? giftTerms.minimumPrice : 1;

    return {
        ...(isNoGiftExperience && (isChooseAnAmount || isVariable)
            ? {
                  amount: requiredNumber()
                      .min(0.01, "should be greater than 0.00")
                      .when([], {
                          is: () => isVariable && minimumPrice > 0,
                          then: (schema) => schema.min(minimumPrice, `should be at least ${minimumPrice}`),
                      }),
              }
            : {}),
        recipientName: recipientRequired ? requiredString() : string().trim(),
        recipientEmail: recipientRequired ? requiredEmail() : string().trim().email(),
    };
};

export const useInvoiceFieldsValidations = (paymentMethod: PaymentMethod): FieldValidations<InvoiceFields> => {
    if (paymentMethod !== PaymentMethod.INVOICE) {
        return { arrivalOffset: number() };
    }

    const earliestArrivalDateTime = useCreatePurchaseStore.getState().getEarliestArrivalDateTime();
    const hasDeposit = useCreatePurchaseStore.getState().hasDeposit();

    return {
        arrivalOffset: daysCantBeInThePast(earliestArrivalDateTime).test(
            "cantBeLessThanBalanceOffset",
            `can't be less than balance due date offset`,
            (value, form) => {
                // this should be checked only if deposit is selected.
                if (value && hasDeposit) {
                    return !(value < form.parent.depositDuration);
                }

                return true;
            },
        ),
    };
};

export const useDepositFieldsValidations = (paymentMethod: PaymentMethod): FieldValidations<DepositFields> => {
    const hasDeposit = useCreatePurchaseStore.getState().hasDeposit();

    if (!hasDeposit) {
        return { deposit: number(), depositDuration: number() };
    }

    const product = useCreatePurchaseStore.getState().getActiveProduct();
    const { dueNow, balance, amount = 0 } = usePurchaseResponseStore.getState().purchase;
    const IsBalanceDueZero = isGiftOfferProduct(product) ? dueNow === 0 : balance === 0;

    const earliestArrivalDateTime = useCreatePurchaseStore.getState().getEarliestArrivalDateTime();
    const isInvoicePurchase = paymentMethod === PaymentMethod.INVOICE;

    return {
        deposit:
            IsBalanceDueZero || !hasDeposit
                ? number()
                : requiredNumberNotEquals(amount, `entered is equal to the purchase amount (${amount})`)
                      .min(0.5)
                      .lessThan(amount, `must be less than the purchase amount (${amount})`),

        depositDuration: hasDeposit
            ? daysCantBeInThePast(earliestArrivalDateTime).test(
                  "cantBeGreaterThanInvoiceOffset",
                  `can't be greater than invoice due date offset`,
                  (value, form) => {
                      // this should be checked only if invoice is selected
                      if (value && isInvoicePurchase) {
                          return !(value > form.parent.arrivalOffset);
                      }

                      return true;
                  },
              )
            : number(),
    };
};

export const useGiftCheckoutValidations = (): any => {
    const seller = getSeller();
    const paymentMethod = useCreatePurchaseStore((state) => state.paymentLineItem.payment.method);
    const commentRequired = isCommentRequired(seller, paymentMethod);

    const customerValidations = useCustomerFieldsValidations(paymentMethod as PaymentMethod);
    const giftValidations = useGiftFieldsValidations();

    return {
        ...customerValidations,
        ...giftValidations,
        tags: array().optional(),
        note: array().optional(),
        comment: commentRequired ? requiredString() : string().trim(),
    };
};

export type MerchandiseCheckoutValidations = FieldValidations<
    DepositFields | InvoiceFields | "tags" | "note" | "comment"
> &
    Partial<FieldValidations<CustomerFields>>;

export const useMerchandiseCheckoutValidations = (): MerchandiseCheckoutValidations => {
    const seller = getSeller();
    const paymentMethod = useCreatePurchaseStore((state) => state.paymentLineItem.payment.method);
    const commentRequired = isCommentRequired(seller, paymentMethod);

    const customerValidations = useCustomerFieldsValidations(paymentMethod as PaymentMethod);
    const depositValidations = useDepositFieldsValidations(paymentMethod as PaymentMethod);
    const invoiceValidations = useInvoiceFieldsValidations(paymentMethod as PaymentMethod);

    return {
        ...customerValidations,
        ...depositValidations,
        ...invoiceValidations,
        tags: array().optional(),
        note: array().optional(),
        comment: commentRequired ? requiredString() : string().trim(),
    };
};

const getCustomerValidations = (isNameRequired: boolean, isEmailRequired: boolean, isPhoneRequired: boolean) => ({
    customerName: isNameRequired ? requiredString() : string().trim(),
    customerEmail: isEmailRequired ? requiredEmail() : nullableEmail(),
    phone: isPhoneRequired ? requiredString() : string().trim(),
});
